import { useTheme } from '../../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import { DashboardStats } from '../../services/dashboardService';

interface Props {
  stats: DashboardStats;
}

export default function PerformanceMetrics({ stats }: Props) {
  const { theme } = useTheme();
  const { t } = useTranslation();

  // Calculate metrics
  const completionRate = stats.totalTickets > 0 
    ? ((stats.ticketsCompleted / stats.totalTickets) * 100).toFixed(1)
    : '0';

  const activeTickets = stats.ticketsByStatus.new + stats.ticketsByStatus.processing;
  const resolvedTickets = stats.ticketsByStatus.resolved + stats.ticketsByStatus.closed;
  
  const urgentTickets = stats.ticketsByPriority.urgent;

  const metrics = [
    {
      id: 'total',
      title: t('pageHeaders.dashboard.totalTickets', 'Total Tickets'),
      value: stats.totalTickets.toString(),
      subtitle: t('pageHeaders.dashboard.allTimeTotal', 'All time total'),
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      color: 'text-blue-500',
      bgColor: theme === 'dark' ? 'bg-blue-900/20' : 'bg-blue-50',
      borderColor: 'border-blue-200',
    },
    {
      id: 'completion',
      title: t('pageHeaders.dashboard.completionRate', 'Completion Rate'),
      value: `${completionRate}%`,
      subtitle: t('pageHeaders.dashboard.ticketsCompleted', 'Tickets completed'),
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      color: 'text-green-500',
      bgColor: theme === 'dark' ? 'bg-green-900/20' : 'bg-green-50',
      borderColor: 'border-green-200',
    },
    {
      id: 'response',
      title: t('pageHeaders.dashboard.avgResponseTime', 'Avg Response Time'),
      value: stats.averageResponseTime,
      subtitle: t('pageHeaders.dashboard.averageFirstResponse', 'Average first response'),
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      color: 'text-purple-500',
      bgColor: theme === 'dark' ? 'bg-purple-900/20' : 'bg-purple-50',
      borderColor: 'border-purple-200',
    },
    {
      id: 'active',
      title: t('pageHeaders.dashboard.activeTickets', 'Active Tickets'),
      value: activeTickets.toString(),
      subtitle: t('pageHeaders.dashboard.newAndProcessing', 'New and processing'),
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      color: 'text-orange-500',
      bgColor: theme === 'dark' ? 'bg-orange-900/20' : 'bg-orange-50',
      borderColor: 'border-orange-200',
    },
  ];

  const getMetricStatus = (id: string, value: string) => {
    switch (id) {
      case 'completion':
        const rate = parseFloat(value);
        if (rate >= 80) return { status: 'excellent', color: 'text-green-600' };
        if (rate >= 60) return { status: 'good', color: 'text-blue-600' };
        if (rate >= 40) return { status: 'fair', color: 'text-orange-600' };
        return { status: 'needs improvement', color: 'text-red-600' };
      case 'active':
        const active = parseInt(value);
        if (active <= 5) return { status: 'low load', color: 'text-green-600' };
        if (active <= 15) return { status: 'moderate load', color: 'text-blue-600' };
        if (active <= 30) return { status: 'high load', color: 'text-orange-600' };
        return { status: 'very high load', color: 'text-red-600' };
      default:
        return { status: '', color: '' };
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {metrics.map((metric) => {
        const status = getMetricStatus(metric.id, metric.value);
        
        return (
          <div
            key={metric.id}
            className={`rounded-lg shadow p-6 border-l-4 ${metric.borderColor} ${metric.bgColor} ${
              theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white'
            } transition-all duration-200 hover:shadow-lg`}
          >
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-3">
                  <div className={`${metric.color}`}>
                    {metric.icon}
                  </div>
                  <div className="flex-1">
                    <p className={`text-sm font-medium ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                    }`}>
                      {metric.title}
                    </p>
                    <div className="flex items-baseline space-x-2">
                      <p className={`text-2xl font-bold ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                      }`}>
                        {metric.value}
                      </p>
                      {status.status && (
                        <span className={`text-xs font-medium ${status.color}`}>
                          {status.status}
                        </span>
                      )}
                    </div>
                    <p className={`text-xs mt-1 ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      {metric.subtitle}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Additional context for specific metrics */}
            {metric.id === 'total' && (
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                <div className="flex justify-between text-xs">
                  <span className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                    {t('pageHeaders.dashboard.resolved', 'Resolved')}: {resolvedTickets}
                  </span>
                  <span className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                    {t('pageHeaders.dashboard.active', 'Active')}: {activeTickets}
                  </span>
                </div>
              </div>
            )}

            {metric.id === 'completion' && (
              <div className="mt-4">
                <div className={`w-full bg-gray-200 rounded-full h-2 ${
                  theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                }`}>
                  <div
                    className="bg-green-500 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min(parseFloat(completionRate), 100)}%` }}
                  ></div>
                </div>
              </div>
            )}

            {metric.id === 'active' && urgentTickets > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                <div className="flex items-center space-x-2">
                  <div className="text-red-500">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <span className={`text-xs font-medium text-red-600 ${
                    theme === 'dark' ? 'text-red-400' : 'text-red-600'
                  }`}>
                    {urgentTickets} {t('pageHeaders.dashboard.urgent', 'urgent')}
                  </span>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Summary Card */}
      <div className={`md:col-span-2 lg:col-span-4 rounded-lg shadow p-6 ${
        theme === 'dark' ? 'bg-gray-800' : 'bg-white'
      }`}>
        <h4 className={`text-lg font-semibold mb-4 ${
          theme === 'dark' ? 'text-white' : 'text-gray-900'
        }`}>
          {t('pageHeaders.dashboard.performanceSummary', 'Performance Summary')}
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Workload Distribution */}
          <div>
            <h5 className={`text-sm font-medium mb-3 ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
            }`}>
              {t('pageHeaders.dashboard.workloadDistribution', 'Workload Distribution')}
            </h5>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className={`text-xs ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  {t('pageHeaders.dashboard.newTickets', 'New')}
                </span>
                <span className={`text-sm font-medium ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                  {stats.ticketsByStatus.new}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className={`text-xs ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  {t('pageHeaders.dashboard.processing', 'Processing')}
                </span>
                <span className={`text-sm font-medium ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                  {stats.ticketsByStatus.processing}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className={`text-xs ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  {t('pageHeaders.dashboard.resolved', 'Resolved')}
                </span>
                <span className={`text-sm font-medium ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                  {stats.ticketsByStatus.resolved}
                </span>
              </div>
            </div>
          </div>

          {/* Priority Breakdown */}
          <div>
            <h5 className={`text-sm font-medium mb-3 ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
            }`}>
              {t('pageHeaders.dashboard.priorityBreakdown', 'Priority Breakdown')}
            </h5>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className={`text-xs text-red-500`}>
                  {t('pageHeaders.dashboard.urgent', 'Urgent')}
                </span>
                <span className={`text-sm font-medium ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                  {stats.ticketsByPriority.urgent}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className={`text-xs text-orange-500`}>
                  {t('pageHeaders.dashboard.high', 'High')}
                </span>
                <span className={`text-sm font-medium ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                  {stats.ticketsByPriority.high}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className={`text-xs text-blue-500`}>
                  {t('pageHeaders.dashboard.medium', 'Medium')}
                </span>
                <span className={`text-sm font-medium ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                  {stats.ticketsByPriority.medium}
                </span>
              </div>
            </div>
          </div>

          {/* Key Insights */}
          <div>
            <h5 className={`text-sm font-medium mb-3 ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
            }`}>
              {t('pageHeaders.dashboard.keyInsights', 'Key Insights')}
            </h5>
            <div className="space-y-2">
              {parseFloat(completionRate) >= 80 && (
                <div className="flex items-center space-x-2">
                  <div className="text-green-500">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <span className={`text-xs ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    {t('pageHeaders.dashboard.excellentCompletion', 'Excellent completion rate')}
                  </span>
                </div>
              )}
              
              {urgentTickets === 0 && (
                <div className="flex items-center space-x-2">
                  <div className="text-green-500">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <span className={`text-xs ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    {t('pageHeaders.dashboard.noUrgentTickets', 'No urgent tickets')}
                  </span>
                </div>
              )}
              
              {activeTickets <= 10 && (
                <div className="flex items-center space-x-2">
                  <div className="text-green-500">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <span className={`text-xs ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    {t('pageHeaders.dashboard.managableWorkload', 'Manageable workload')}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
